import React, { useCallback, useState } from 'react';
import { ConfigProvider, Layout } from 'antd';
import { ConfigProvider as MobileConfigProvider } from 'antd-mobile';
import {
  ApiOutlined,
  BellOutlined,
  CopyOutlined,
  DollarCircleOutlined,
  ExceptionOutlined,
  GiftOutlined,
  LineChartOutlined,
  ProfileOutlined,
  TeamOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { Outlet } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';
import { currentUserVar } from '@/graphql/operations/user';
import Sidebar from './Sidebar';
import Header from './Header';
import BaseLayoutContext from '@/context/LegacyLayoutContext';
import styles from './BaseLayout.module.pcss';
import { MenuItem } from '@/types';
import enUS from 'antd/locale/en_US';
import enUSMobile from 'antd-mobile/es/locales/en-US';
import { organizationConfig, whiteLabelingOrganisations } from '@/context/AdminLayoutContext';
import { getPeopleMenuPath } from '@/utils/getPeopleMenuPath';

const { Content } = Layout;

const allowedOrgIds = ['77c8f365-9468-44b5-8fb2-ea31604e3bff'];

const menuItems: MenuItem[] = [
  {
    title: 'My Plans',
    path: '/v1/plans',
    icon: <ProfileOutlined />,
    admin: true,
    participant: false,
  },
  {
    title: 'My Earnings',
    path: '/v1/payouts',
    icon: <DollarCircleOutlined />,
    admin: true,
    participant: false,
  },
  {
    title: 'My Rewards',
    path: '/v1/rewards',
    icon: <GiftOutlined />,
    admin: false,
    participant: true,
  },
  {
    title: 'Connect',
    path: '/v1/connect',
    icon: <ApiOutlined />,
    admin: true,
    participant: false,
  },
  {
    title: 'Users',
    path: '/v1/users',
    icon: <TeamOutlined />,
    admin: true,
    participant: false,
  },
  {
    title: 'Analytics',
    path: '/v1/analytics',
    icon: <LineChartOutlined />,
    admin: true,
    participant: false,
  },
  {
    title: 'Notifications',
    path: '/v1/notifications',
    icon: <BellOutlined />,
    admin: true,
    participant: false,
  },
  {
    title: 'Events',
    path: '/v1/events',
    icon: <ExceptionOutlined />,
    admin: true,
    participant: false,
  },
  {
    title: 'Templates',
    path: '/v1/templates',
    icon: <CopyOutlined />,
    admin: true,
    participant: false,
  },
  {
    title: 'Widgets',
    path: '/v1/widgets',
    icon: <ToolOutlined />,
    admin: true,
    participant: false,
  },
];

export default function BaseLayout(): React.ReactElement {
  const currentUser = useReactiveVar(currentUserVar);

  const orgName = currentUser.organization.name as 'ARYA';

  const [isCollapsed, setCollapsed] = useState(false);

  const peopleMenuPath = getPeopleMenuPath(currentUser);

  const getContext = useCallback(() => {
    return {
      layoutConfig:
        currentUser.permissions?.isAdmin || !whiteLabelingOrganisations.includes(currentUser.organization.name)
          ? organizationConfig['ARYA']
          : organizationConfig[orgName],
      siderWidth: isCollapsed ? 80 : 200,
    };
  }, [currentUser.permissions?.isAdmin, currentUser.organization.name, orgName, isCollapsed]);

  const filteredMenus = menuItems.filter((item) => {
    if (process.env.ARYA_PLATFORM === 'production' && !allowedOrgIds.includes(currentUser.organization.id)) {
      return item.title !== 'Templates';
    }
    return item;
  });

  const menusWithUpdatedUsersRoute = filteredMenus.map((item) => {
    if (item.title === 'Users') {
      return {
        ...item,
        path: peopleMenuPath === '/employees' ? '/employees' : '/v1/users',
      };
    }

    return item;
  });

  const toggleSideBar = useCallback(() => {
    setCollapsed((prevState) => !prevState);
  }, []);

  return (
    <BaseLayoutContext.Provider value={getContext()}>
      <ConfigProvider
        locale={enUS}
        theme={{
          token: {
            fontFamily: 'Inter',
            colorPrimary: '#202020',
          },
          components: {
            Select: {
              optionSelectedBg: '#A1ABB7',
            },
          },
        }}
      >
        <MobileConfigProvider locale={enUSMobile}>
          <Layout style={{ minHeight: '100vh' }}>
            <Sidebar menuItems={menusWithUpdatedUsersRoute} isCollapsed={isCollapsed} toggleSideBar={toggleSideBar} />
            <Layout className={styles.layout}>
              <Header isCollapsed={isCollapsed} toggleSideBar={toggleSideBar} currentUser={currentUser} />
              <Content className={styles.content}>
                <Outlet />
              </Content>
            </Layout>
          </Layout>
        </MobileConfigProvider>
      </ConfigProvider>
    </BaseLayoutContext.Provider>
  );
}
